/**
 * Custom Multi Select
 */
/* eslint-disable @wordpress/no-global-active-element -- This rule only males sence for files that use React */

class MultiSelect {
	/**
	 * Default settings for the MultiSelect component.
	 *
	 * @type {Object}
	 * @property {string} wrapperClass        - The class of the main wrapper.
	 * @property {string} buttonClass         - The base class for the button element.
	 * @property {string} buttonLabelClass    - The class for the label text inside the button.
	 * @property {string} buttonStyle         - Default style attribute for the button.
	 * @property {string} buttonVariant       - Default variant (outline, etc.) for the button.
	 * @property {string} dropdownClass       - The base class for the dropdown element.
	 * @property {string} dropdownTopClass    - Class added if the dropdown is placed above the button.
	 * @property {string} listClass           - The base class for the `<ul>` list of options.
	 * @property {string} itemClass           - The base class for each `<li>` item.
	 * @property {string} optgroupClass       - Class applied to optgroup labels.
	 * @property {string} optionClass         - Class applied to normal option items.
	 * @property {string} searchClass         - Wrapper class for the search feature (if enabled).
	 * @property {string} searchInputClass    - Class for the text input used to search.
	 * @property {string} searchButtonClass   - Class for the search button element.
	 * @property {string} noResultsClass      - Class for the "No Results" item.
	 * @property {number} minDropdownHeight   - Minimum dropdown height in pixels.
	 * @property {number} maxDropdownHeight   - Maximum dropdown height in pixels.
	 * @property {number} bufferSpace         - Space to keep from viewport edges in pixels.
	 * @property {number} searchDebounceDelay - Debounce delay for search input in milliseconds.
	 */
	static defaults = {
		wrapperClass: 'awc-select__wrapper',
		buttonClass: 'awc-select__button',
		buttonLabelClass: 'awc-select__button-label',
		buttonStyle: 'square',
		buttonVariant: 'outline',
		dropdownClass: 'awc-select__dropdown',
		dropdownTopClass: 'awc-select--top',
		listClass: 'awc-select__list',
		itemClass: 'awc-select__item',
		optgroupClass: 'awc-select__item--optgroup',
		optionClass: 'awc-select__item--option',
		searchClass: 'awc-select__search',
		searchInputClass: 'awc-select__search-input',
		searchButtonClass: 'awc-select__search-btn',
		noResultsClass: 'awc-select__item--no-results',
		minDropdownHeight: 200,
		maxDropdownHeight: 300,
		bufferSpace: 20,
		searchDebounceDelay: 200,
		optionFormatter: false,
	};

	/**
	 * Constructor for the MultiSelect component.
	 *
	 * @param {Object}      settings         - Configuration overrides for the MultiSelect.
	 * @param {HTMLElement} settings.element - The container element that wraps the native <select>.
	 */
	constructor( settings ) {
		this.settings = { ...MultiSelect.defaults, ...settings };
		this.element = this.settings.element;
		this.select = this.element.querySelector( 'select' );
		this.changed = false;

		// Validate required elements
		if ( ! this.select ) {
			throw new Error( 'MultiSelect: <select> element not found' );
		}

		// Store instance reference on the element
		this.element._multiSelectInstance = this;

		this.selectId = this.select.getAttribute( 'id' );
		this.optGroups = this.select.querySelectorAll( 'optgroup' );
		this.options = [ ...this.select.querySelectorAll( 'option' ) ];
		this.button = null;
		this.buttonLabel = null;
		this.dropdown = null;
		this.wrapper = null;
		this.customOptions = [];
		this._visibleOptionsCache = null;

		/**
		 * Holds references to each `.js-select__item` → `.js-select__checkbox` for quick lookup.
		 * @type {Map<HTMLElement, HTMLInputElement>}
		 */
		this.checkboxes = new Map();

		this.label = document.querySelector( `[for="${ this.selectId }"]` );
		this.labelContent = '';
		this.selectedOptCounter = 0;
		this.optionIndex = 0;
		this.lastMatchingIndex = 0;
		this.lastTypedChar = '';
		this.isMouseDown = false;

		// Store bound event handlers for cleanup
		this.boundWindowClickHandler = null;
		this.boundFocusOutHandler = null;
		this.boundLabelClickHandler = null;
		this.boundButtonClickHandler = null;
		this.boundButtonKeydownHandler = null;
		this.boundDropdownMousedownHandler = null;
		this.boundDropdownMouseupHandler = null;
		this.boundDropdownClickHandler = null;
		this.boundDropdownKeydownHandler = null;
		this.boundSelectChangeHandler = null;
		this.boundSearchInputHandler = null;
		this.searchDebounceTimeout = null;
		this.transitionEndCallback = null;
		this.focusTimeoutId = null;

		if ( this.label ) {
			this.labelContent = this.label.textContent;
		}

		// Button label options
		this.noSelectedLabel =
			this.element.getAttribute( 'data-no-selected-label' ) || 'Select options';
		this.multiSelectedLabel =
			this.element.getAttribute( 'data-multi-selected-label' ) || '{n} items selected';
		this.multiSelectedNumber = this.element.getAttribute( 'data-multi-selected-n' ) || 1;
		this.noUpdateLabel = this.element.getAttribute( 'data-update-label' ) === 'off';
		this.insetLabel = this.element.getAttribute( 'data-inset-label' ) === 'on';
		this.search = this.element.getAttribute( 'data-search' ) === 'on';
		this.searchLabel = this.element.getAttribute( 'data-search-label' ) || 'Search...';
		this.noResultsLabel =
			this.element.getAttribute( 'data-no-results-label' ) || 'No results found...';

		// Escape HTML in user-provided labels for security
		this.noSelectedLabel = this.escapeHtmlInConstructor( this.noSelectedLabel );
		this.multiSelectedLabel = this.escapeHtmlInConstructor( this.multiSelectedLabel );
		this.searchLabel = this.escapeHtmlInConstructor( this.searchLabel );
		this.noResultsLabel = this.escapeHtmlInConstructor( this.noResultsLabel );

		this.initSelect();
		this.initSelectEvents();
	}

	/**
	 * Escapes HTML special characters to prevent XSS attacks.
	 * Used during construction before DOM is available.
	 * @private
	 * @param {string} text - The text to escape.
	 * @return {string} HTML-escaped text.
	 */
	escapeHtmlInConstructor( text ) {
		return text
			.replace( /&/g, '&amp;' )
			.replace( /</g, '&lt;' )
			.replace( />/g, '&gt;' )
			.replace( /"/g, '&quot;' )
			.replace( /'/g, '&#039;' );
	}

	/**
	 * Injects the custom markup for the MultiSelect UI and stores relevant elements.
	 * - Builds the button and dropdown HTML.
	 * - Hides the native <select> and arrow icon.
	 * - Initializes necessary references (like this.customOptions).
	 * @private
	 */
	initSelect() {
		// Create HTML for custom select
		const selectHtml = `
              <div class="${ this.settings.wrapperClass }">
                  ${ this.selectButton() }
                  ${ this.selectDropdown() }
              </div>`;
		this.element.insertAdjacentHTML( 'beforeend', selectHtml );

		// Save custom elements
		this.wrapper = this.element.querySelector( `.${ this.settings.wrapperClass }` );
		this.button = this.element.querySelector( `.${ this.settings.buttonClass }` );
		this.buttonLabel = this.button.querySelector( `.${ this.settings.buttonLabelClass }` );
		this.dropdown = this.element.querySelector( `.${ this.settings.dropdownClass }` );
		this.customOptions = [
			...this.dropdown.querySelectorAll( `.${ this.settings.optionClass }` ),
		];
		this.customOptions.forEach( ( option ) => {
			const checkbox = option.querySelector( 'input[type=checkbox]' );
			if ( checkbox ) {
				this.checkboxes.set( option, checkbox );
			}
		} );
		this.searchInput = this.dropdown.querySelector( `.${ this.settings.searchInputClass }` );
		this.searchButton = this.dropdown.querySelector( `.${ this.settings.searchButtonClass }` );
		this.noResults = this.dropdown.querySelector( `.${ this.settings.noResultsClass }` );

		// Place dropdown
		this.placeDropdown();
	}

	/**
	 * Attaches all event listeners needed to operate the MultiSelect:
	 * - Click/keydown on the button to toggle or open the dropdown.
	 * - Click on dropdown items/checkboxes to select/deselect.
	 * - Keydown within the dropdown for navigation.
	 * - External 'change' on the <select> to sync UI.
	 * - Search input changes.
	 * - Window click to close dropdown when clicking outside.
	 * @private
	 */
	initSelectEvents() {
		// Focus on button when label is clicked
		if ( this.label ) {
			this.boundLabelClickHandler = () => this.moveFocus( this.button );
			this.label.addEventListener( 'click', this.boundLabelClickHandler );
		}

		// Toggle open on click
		this.boundButtonClickHandler = () => this.toggleDropdown();
		this.button.addEventListener( 'click', this.boundButtonClickHandler );

		// Toggle on specific keys
		this.boundButtonKeydownHandler = ( event ) => this.handleOpenKeyPress( event );
		this.button.addEventListener( 'keydown', this.boundButtonKeydownHandler );

		// Track mouse down state
		this.boundDropdownMousedownHandler = () => ( this.isMouseDown = true );
		this.dropdown.addEventListener( 'mousedown', this.boundDropdownMousedownHandler );

		this.boundDropdownMouseupHandler = () => ( this.isMouseDown = false );
		this.dropdown.addEventListener( 'mouseup', this.boundDropdownMouseupHandler );

		// Handle clicks on the dropdown
		this.boundDropdownClickHandler = ( event ) => {
			const option = event.target.closest( `.${ this.settings.optionClass }` );

			if ( ! option ) {
				return;
			}

			// Prevent default behavior for checkboxes and labels
			event.preventDefault();
			this.selectOption( option );
		};
		this.dropdown.addEventListener( 'click', this.boundDropdownClickHandler );

		// Keyboard nav in dropdown
		this.boundDropdownKeydownHandler = ( event ) => this.handleDropdownKeyPress( event );
		this.dropdown.addEventListener( 'keydown', this.boundDropdownKeydownHandler );

		// Listen for external changes to the <select>
		this.boundSelectChangeHandler = () => this.syncUIWithNativeSelect();
		this.select.addEventListener( 'change', this.boundSelectChangeHandler );

		// Search with inline debounce
		if ( this.searchInput ) {
			this.boundSearchInputHandler = () => {
				clearTimeout( this.searchDebounceTimeout );
				this.searchDebounceTimeout = setTimeout(
					() => this.filterOptions(),
					this.settings.searchDebounceDelay
				);
			};
			this.searchInput.addEventListener( 'input', this.boundSearchInputHandler );
		}

		// Close if clicking outside
		this.boundWindowClickHandler = ( event ) =>
			! this.wrapper.contains( event.target ) &&
			this.isDropdownOpen() &&
			this.toggleDropdown( false );
		window.addEventListener( 'click', this.boundWindowClickHandler );

		// Close when focus leaves the component
		this.boundFocusOutHandler = () => {
			// Don't close if we're in the middle of a mouse interaction
			if ( this.isMouseDown ) {
				return;
			}

			// Clear any existing timeout
			if ( this.focusTimeoutId ) {
				clearTimeout( this.focusTimeoutId );
			}

			// Use timeout to ensure focus has settled after tab navigation
			this.focusTimeoutId = setTimeout( () => {
				// Only close if focus is truly outside and dropdown is still open
				const focusedElement = this.wrapper.ownerDocument.activeElement;
				const isOutside = ! this.wrapper.contains( focusedElement );

				if ( isOutside && this.isDropdownOpen() && ! this.isMouseDown ) {
					this.toggleDropdown( false );
				}
				this.focusTimeoutId = null;
			}, 50 );
		};
		this.wrapper.addEventListener( 'focusout', this.boundFocusOutHandler );
	}

	/**
	 * Creates the HTML string for the main button element:
	 * - Responsible for toggling the dropdown
	 * - Displays currently selected items in the label
	 * @private
	 * @return {string} HTML markup for the button.
	 */
	selectButton() {
		// Determine button class based on whether data-button-class attribute exist
		const buttonClass = this.element.getAttribute( 'data-button-class' )
			? `${ this.settings.buttonClass } ${ this.element.getAttribute( 'data-button-class' ) }`
			: this.settings.buttonClass;

		// Determine button style based on whether data-button-style attribute exist
		const buttonStyle = this.element.getAttribute( 'data-button-style' )
			? this.element.getAttribute( 'data-button-style' )
			: this.settings.buttonStyle;

		// Determine button variant based on whether data-button-variant attribute exist
		const buttonVariant = this.element.getAttribute( 'data-button-variant' )
			? this.element.getAttribute( 'data-button-variant' )
			: this.settings.buttonVariant;

		// Add aria label if a label is associated with this select
		const ariaLabel = this.labelContent ? `aria-label="${ this.labelContent }"` : '';

		return `
              <button
                  class="${ buttonClass }"
                  data-style="${ buttonStyle }"
                  data-variant="${ buttonVariant }"
                  type="button"
                  role="combobox"
                  ${ ariaLabel }
                  aria-controls="${ this.selectId }-dropdown"
                  aria-haspopup="listbox"
                  aria-expanded="false"
                  tabindex="0">
                      <span class="${ this.settings.buttonLabelClass }">
                          ${ this.getSelectedOptionText() }
                      </span>
              </button>`;
	}

	/**
	 * Creates the dropdown HTML string containing the search bar (if enabled) and the list of options.
	 * @private
	 * @return {string} The full dropdown markup, including <ul>.
	 */
	selectDropdown() {
		// Build the search markup if enabled
		let searchMarkup = '';
		if ( this.search ) {
			searchMarkup = `
          <div class="${ this.settings.searchClass }">
            <input
              type="search"
              class="${ this.settings.searchInputClass } f-control"
              placeholder="${ this.searchLabel }"
              aria-label="${ this.searchLabel }">
            <button class="${ this.settings.searchButtonClass }" aria-label="Search"></button>
          </div>
        `;
		}

		// Build list items, possibly grouped by <optgroup>
		let listItems = '';
		if ( this.optGroups.length > 0 ) {
			this.optGroups.forEach( ( optGroup ) => {
				const optGroupLabel = optGroup.label;
				const optGroupOptions = [ ...optGroup.querySelectorAll( 'option' ) ];

				listItems += `
                    <li class="${ this.settings.itemClass } ${ this.settings.optgroupClass }">
                        <span>${ optGroupLabel }</span>
                    </li>
                `;
				listItems += this.buildOptionsList( optGroupOptions );
			} );
		} else {
			listItems += this.buildOptionsList( this.options );
		}

		// Add a no-results item if searching is enabled or if there are no options
		if ( this.search || this.options.length === 0 ) {
			listItems += `
                <li
                    class="${ this.settings.itemClass } ${ this.settings.noResultsClass }"
                    aria-hidden="${ this.options.length > 0 }"
                >
                    <span>${ this.noResultsLabel }</span>
                </li>
            `;
		}

		return `
        <div id="${ this.selectId }-dropdown" class="${ this.settings.dropdownClass }">
            ${ searchMarkup }
            <ul
              class="${ this.settings.listClass }"
              role="listbox"
              aria-multiselectable="true">
              ${ listItems }
            </ul>
        </div>`;
	}

	/**
	 * Builds the `<li>` list markup for a given set of <option> elements.
	 * Each item includes a visually hidden checkbox and a label for text.
	 *
	 * @private
	 * @param {HTMLOptionElement[]} options - An array of <option> elements to transform into <li> items.
	 * @return {string} The HTML markup for the generated list items.
	 */
	buildOptionsList( options ) {
		let list = '';

		// If no options, return empty string - the message will be handled in selectDropdown
		if ( ! options || options.length === 0 ) {
			return '';
		}

		// Iterate over each option and construct the HTML for it
		options.forEach( ( option ) => {
			const selected = option.selected ? 'aria-selected="true"' : 'aria-selected="false"';
			const disabled = option.disabled ? 'disabled' : '';
			const checked = option.selected ? 'checked' : '';

			// Format the option text if formatter is provided
			let optionText = option.text;
			if ( typeof this.settings.optionFormatter === 'function' ) {
				optionText = this.settings.optionFormatter( option.text, option );
			}

			// Get all data-* attributes from the option element
			let dataAttributes = '';
			Array.from( option.attributes ).forEach( ( attr ) => {
				if (
					attr.name.startsWith( 'data-' ) &&
					attr.name !== 'data-value' &&
					attr.name !== 'data-index' &&
					attr.name !== 'data-label'
				) {
					dataAttributes += ` ${ attr.name }="${ attr.value }"`;
				}
			} );

			list += `
                <li
                    class="${ this.settings.itemClass } ${ this.settings.optionClass }"
                    role="option"
                    data-value="${ option.value }"
                    data-index="${ this.optionIndex }"
                    data-label="${ optionText }"
                    ${ dataAttributes }
                    ${ selected }
                >
                    <input
                        type="checkbox"
                        id="${ this.selectId }-option-${ this.optionIndex }"
                        ${ checked }
                        ${ disabled }
                        tabindex="-1"
                    />
                    <label for="${ this.selectId }-option-${ this.optionIndex }">
				        <i></i>
                        <span>${ optionText }</span>
                    </label>
              </li>
              `;

			this.optionIndex++;
		} );

		return list;
	}

	/**
	 * Filters (shows/hides) the list items in the dropdown based on user input.
	 * If no items match, the .noResults item is shown.
	 * @private
	 */
	filterOptions() {
		if ( ! this.searchInput ) {
			return;
		}

		const query = this.searchInput.value.trim().toLowerCase();
		if ( ! query ) {
			// If no query, show all items, hide 'no results'
			this.customOptions.forEach( ( item ) => item.removeAttribute( 'aria-hidden' ) );
			this.toggleNoResultsMessage( true );
			this._visibleOptionsCache = null;
			return;
		}

		let hasVisibleOptions = false;
		this.customOptions.forEach( ( item ) => {
			const text = item.textContent.toLowerCase();
			const matches = text.includes( query );

			item.setAttribute( 'aria-hidden', ! matches );

			if ( matches ) {
				hasVisibleOptions = true;
			}
		} );

		this.toggleNoResultsMessage( hasVisibleOptions );
		this._visibleOptionsCache = null;
	}

	/**
	 * Toggles the visibility of the 'no results' message based on the search outcome.
	 * @private
	 * @param {boolean} hasVisibleOptions - Whether there is at least one matching item.
	 */
	toggleNoResultsMessage( hasVisibleOptions ) {
		if ( this.noResults ) {
			this.noResults.setAttribute( 'aria-hidden', hasVisibleOptions );
		}
	}

	/**
	 * Gets only the visible (not filtered/hidden) options.
	 * Results are cached and invalidated when filterOptions() runs.
	 * @private
	 * @return {HTMLElement[]} Array of visible option elements.
	 */
	getVisibleOptions() {
		if ( ! this._visibleOptionsCache ) {
			this._visibleOptionsCache = Array.from( this.customOptions ).filter(
				( option ) => option.getAttribute( 'aria-hidden' ) !== 'true'
			);
		}
		return this._visibleOptionsCache;
	}

	/**
	 * Opens or closes the dropdown based on the provided boolean or current state.
	 * - Also handles focusing the selected item, placing the dropdown, etc.
	 * @private
	 * @param {boolean} bool - If true/false, forces the dropdown open/closed; otherwise toggles.
	 */
	toggleDropdown( bool ) {
		const ariaExpanded =
			typeof bool === 'boolean'
				? bool
				: this.button.getAttribute( 'aria-expanded' ) !== 'true';

		this.button.setAttribute( 'aria-expanded', ariaExpanded.toString() );

		// If the dropdown is opening
		if ( ariaExpanded ) {
			this.changed = false;

			// Determine what element to focus
			const elementToFocus = this.searchInput || this.getSelectedOption();

			// Focus immediately
			if ( elementToFocus ) {
				this.moveFocus( elementToFocus );
			}

			// Remove previous transition callback if it exists
			if ( this.transitionEndCallback ) {
				this.dropdown.removeEventListener( 'transitionend', this.transitionEndCallback );
			}

			// Ensure focus is maintained after transitions
			this.transitionEndCallback = () => {
				if ( elementToFocus ) {
					this.moveFocus( elementToFocus );
				}
				this.dropdown.removeEventListener( 'transitionend', this.transitionEndCallback );
				this.transitionEndCallback = null;
			};
			this.dropdown.addEventListener( 'transitionend', this.transitionEndCallback );

			// Position the dropdown (up or down) based on available space
			this.placeDropdown();
		} else {
			// Clean up transition callback when closing
			if ( this.transitionEndCallback ) {
				this.dropdown.removeEventListener( 'transitionend', this.transitionEndCallback );
				this.transitionEndCallback = null;
			}

			// Dispatch closed event
			document.dispatchEvent(
				new CustomEvent( 'awc-select-closed', {
					bubbles: true,
					detail: { select: this.select, changed: this.changed },
				} )
			);
		}
	}

	/**
	 * Positions the dropdown below if possible, else above if possible.
	 * If neither side has enough space, use whichever side has more space.
	 * The dropdown's max-height is capped at maxDropdownHeight, but may be less if space is limited.
	 * @private
	 */
	placeDropdown() {
		const buttonRect = this.button.getBoundingClientRect();

		// Calculate available space below and above the button
		const spaceBelow = window.innerHeight - buttonRect.bottom - this.settings.bufferSpace;
		const spaceAbove = buttonRect.top - this.settings.bufferSpace;

		// Decide direction: move up if below doesn't meet minimum AND is less than above
		const moveUp = spaceBelow < this.settings.minDropdownHeight && spaceBelow < spaceAbove;
		const availableSpace = moveUp ? spaceAbove : spaceBelow;

		// Set height to smallest of: available space, actual content size, or configured maximum
		const dropdownMaxHeight = Math.min( availableSpace, this.settings.maxDropdownHeight );

		// Toggle the "move-up" class based on positioning decision
		this.element.classList.toggle( this.settings.dropdownTopClass, moveUp );

		// Apply the calculated height
		this.dropdown.setAttribute( 'style', `max-height: ${ dropdownMaxHeight }px;` );
	}

	/**
	 * Handles key presses on the main button to open the dropdown using certain keys.
	 * @private
	 * @param {KeyboardEvent} event
	 */
	handleOpenKeyPress( event ) {
		const { key } = event;
		const openKeys = [ 'ArrowDown', 'ArrowUp', 'Enter', ' ' ];

		if ( ! this.isDropdownOpen() && openKeys.includes( key ) ) {
			event.preventDefault();
			this.toggleDropdown( true );
		}
	}

	/**
	 * Handles keydown events within the dropdown for navigation, closing on escape, etc.
	 * @private
	 * @param {KeyboardEvent} event
	 */
	handleDropdownKeyPress( event ) {
		const { key } = event;
		switch ( key ) {
			case 'Escape':
				this.toggleDropdown( false );
				this.moveFocus( this.button );
				break;
			case 'Tab':
				this.handleTabKeyPress( event );
				break;
			case 'ArrowUp':
				this.handleDirectionKeyPress( 'prev', event );
				break;
			case 'ArrowDown':
				this.handleDirectionKeyPress( 'next', event );
				break;
			case ' ':
				this.handleSpaceKeyPress( event );
				break;
			case 'Enter':
				this.handleSpaceKeyPress( event );
				break;
			default:
				if (
					this.searchInput &&
					this.searchInput.ownerDocument.activeElement === this.searchInput
				) {
					// Let search input handle typed characters
					break;
				}
				this.handleAlphanumericKeyPress( key );
				break;
		}
	}

	/**
	 * Handles Tab key press to navigate between search input, search button, and options.
	 * @private
	 * @param {KeyboardEvent} event
	 */
	handleTabKeyPress( event ) {
		const activeElement = this.dropdown.ownerDocument.activeElement;

		// If search exists (input + button)
		if ( this.searchInput && this.searchButton ) {
			if ( activeElement === this.searchInput ) {
				event.preventDefault();
				// Tab from search input → search button
				this.moveFocus( this.searchButton );
			} else if ( activeElement === this.searchButton ) {
				if ( event.shiftKey ) {
					event.preventDefault();
					// Shift+Tab from search button → search input
					this.moveFocus( this.searchInput );
				} else {
					event.preventDefault();
					// Tab from search button → first visible option
					const visibleOptions = this.getVisibleOptions();
					const firstOption =
						visibleOptions[ 0 ]?.querySelector( 'input[type=checkbox]' );

					if ( firstOption ) {
						this.moveFocus( firstOption );
					}
				}
			} else {
				// We're on an option
				const currentItem = activeElement.closest( `.${ this.settings.optionClass }` );
				const visibleOptions = this.getVisibleOptions();
				const currentIndex = visibleOptions.indexOf( currentItem );

				if ( event.shiftKey ) {
					event.preventDefault();
					// Shift+Tab from first visible option → search button, otherwise previous option
					if ( currentIndex === 0 ) {
						this.moveFocus( this.searchButton );
					} else {
						this.handleDirectionKeyPress( 'prev', event );
					}
				} else if ( currentIndex === visibleOptions.length - 1 ) {
					// Tab from last visible option → close dropdown and let focus move naturally
					this.toggleDropdown( false );
					this.moveFocus( this.button );
					// Don't prevent default - let Tab move to next element
				} else {
					event.preventDefault();
					this.handleDirectionKeyPress( 'next', event );
				}
			}
		} else if ( event.shiftKey ) {
			// No search input, just navigate between options
			this.handleDirectionKeyPress( 'prev', event );
		} else {
			this.handleDirectionKeyPress( 'next', event );
		}
	}

	/**
	 * Moves focus up/down to the next/previous item in the dropdown.
	 * @private
	 * @param {"prev"|"next"} direction - The direction to move focus.
	 * @param {KeyboardEvent} event     - The keydown event object.
	 */
	handleDirectionKeyPress( direction, event ) {
		event.preventDefault();

		const visibleOptions = this.getVisibleOptions();

		let index = visibleOptions.indexOf(
			document.activeElement.closest( `.${ this.settings.optionClass }` )
		);
		index = direction === 'next' ? index + 1 : index - 1;

		if ( index < 0 ) {
			index = visibleOptions.length - 1;
		}
		if ( index >= visibleOptions.length ) {
			index = 0;
		}

		this.moveFocus( visibleOptions[ index ].querySelector( 'input[type=checkbox]' ) );
	}

	/**
	 * Toggles selection on the currently focused item when space or Enter is pressed.
	 * @private
	 * @param {KeyboardEvent} event
	 */
	handleSpaceKeyPress( event ) {
		const focusedItem = document.activeElement.closest(
			`.${ this.settings.optionClass } label`
		);

		if ( ! focusedItem ) {
			return;
		}

		event.preventDefault();
		this.selectOption( focusedItem );
	}

	/**
	 * Jumps to the first option whose label starts with the typed character, cycling if multiple match.
	 * @private
	 * @param {string} key - The typed character.
	 */
	handleAlphanumericKeyPress( key ) {
		const typedChar = key.toLowerCase();
		if ( this.lastTypedChar !== typedChar ) {
			this.lastMatchingIndex = 0;
		}

		const visibleOptions = this.getVisibleOptions();
		const matchingOptions = visibleOptions.filter( ( option ) => {
			const text = option.querySelector( 'span' )?.textContent.toLowerCase() || '';
			return text.startsWith( typedChar );
		} );

		if ( matchingOptions.length ) {
			if ( this.lastMatchingIndex >= matchingOptions.length ) {
				this.lastMatchingIndex = 0;
			}
			const target = matchingOptions[ this.lastMatchingIndex++ ];
			this.moveFocus( target.querySelector( 'input[type=checkbox]' ) );
		}

		this.lastTypedChar = typedChar;
	}

	/**
	 * Selects or deselects a given option item in the dropdown,
	 * updates the native <select>, and dispatches the necessary events.
	 * @private
	 * @param {HTMLElement} option - The `.awc-select__item--option label` element to toggle.
	 */
	selectOption( option ) {
		this.changed = true;

		const isSelected = option.getAttribute( 'aria-selected' ) === 'true';
		const checkbox = option.querySelector( 'input[type=checkbox]' );

		if ( isSelected ) {
			// Deselect option
			option.setAttribute( 'aria-selected', 'false' );
			if ( checkbox ) {
				checkbox.checked = false;
			}
			this.updateNativeSelect( option.getAttribute( 'data-index' ), false );
		} else {
			// Select option
			option.setAttribute( 'aria-selected', 'true' );
			if ( checkbox ) {
				checkbox.checked = true;
			}
			this.updateNativeSelect( option.getAttribute( 'data-index' ), true );
		}

		// Update label text
		if ( this.buttonLabel ) {
			const selectLabel = this.getSelectedOptionText();
			this.buttonLabel.innerHTML = selectLabel;

			// Toggle active class if there's at least 1 selection
			this.button.classList.toggle( 'select__label--active', this.selectedOptCounter > 0 );
		}

		// Dispatch events for external scripts
		option.dispatchEvent(
			new CustomEvent( 'change', {
				bubbles: true,
				detail: {
					selected: ! isSelected,
					value: option.getAttribute( 'data-value' ),
				},
			} )
		);
		this.select.dispatchEvent( new Event( 'change', { bubbles: true } ) );
		this.select.dispatchEvent( new Event( 'input', { bubbles: true } ) );
	}

	/**
	 * Syncs the custom dropdown UI with the current state of <select>.
	 * Called whenever an external change happens on the <select> (e.g., via code or tags).
	 * @private
	 */
	syncUIWithNativeSelect() {
		this.options.forEach( ( option, index ) => {
			const item = this.dropdown.querySelector(
				`.${ this.settings.optionClass } label[data-index="${ index }"]`
			);
			if ( ! item ) {
				return;
			}

			const isSelected = item.getAttribute( 'aria-selected' ) === 'true';
			if ( option.selected && ! isSelected ) {
				// If <select> says it's selected, but the item is not
				item.setAttribute( 'aria-selected', 'true' );
				const checkbox = this.checkboxes.get( item );
				if ( checkbox ) {
					checkbox.checked = true;
				}
			} else if ( ! option.selected && isSelected ) {
				// If <select> says it's not selected, but the item is
				item.setAttribute( 'aria-selected', 'false' );
				const checkbox = this.checkboxes.get( item );
				if ( checkbox ) {
					checkbox.checked = false;
				}
			}
		} );

		// Update the dropdown button label
		if ( this.buttonLabel ) {
			const labelText = this.getSelectedOptionText();
			this.buttonLabel.innerHTML = labelText;
		}
	}

	/**
	 * Updates the selected property of the correct native <option> object(s).
	 * Does not dispatch events (those happen in selectOption()) to avoid duplication.
	 *
	 * @private
	 * @param {number|string} index - The index of the <option> to update (or null/undefined to update all).
	 * @param {boolean}       bool  - Whether the option(s) should be selected (true) or deselected (false).
	 */
	updateNativeSelect( index, bool ) {
		if ( index === null || index === undefined ) {
			this.options.forEach( ( opt ) => ( opt.selected = bool ) );
		} else {
			this.options[ index ].selected = bool;
		}
	}

	/**
	 * Gets the first selected `.js-select__item`'s checkbox or the first visible checkbox if none are selected.
	 * @private
	 * @return {HTMLElement|null} The checkbox of a selected item or the first visible item if none are selected.
	 */
	getSelectedOption() {
		const selectedOption = this.dropdown.querySelector( '[aria-selected="true"]' );

		if ( selectedOption ) {
			return selectedOption.querySelector( 'input[type=checkbox]' );
		}

		const visibleOptions = this.getVisibleOptions();
		return visibleOptions[ 0 ]?.querySelector( 'input[type=checkbox]' ) || null;
	}

	/**
	 * Builds the text label to display in the button based on the currently selected options.
	 * Note: Labels are already HTML-escaped in the constructor for security.
	 * @private
	 * @return {string} HTML string representing the selected options in a user-friendly way.
	 */
	getSelectedOptionText() {
		const noSelectionText = `<span class="term">${ this.noSelectedLabel }</span>`;
		if ( this.noUpdateLabel ) {
			return noSelectionText;
		}

		this.selectedOptCounter = 0;
		let text = '';
		this.options.forEach( ( option ) => {
			if ( option.selected ) {
				if ( this.selectedOptCounter > 0 ) {
					text += ', ';
				}
				// option.text comes from native select, already safe
				text += option.text;
				this.selectedOptCounter++;
			}
		} );

		if ( this.selectedOptCounter > this.multiSelectedNumber ) {
			const labelWithCount = this.multiSelectedLabel.replace(
				'{n}',
				this.selectedOptCounter
			);
			text = `<span class="details">${ labelWithCount }</span>`;
		} else if ( this.selectedOptCounter > 0 ) {
			// Escape the concatenated option text for safety
			const div = document.createElement( 'div' );
			div.textContent = text;
			text = `<span class="details">${ div.innerHTML }</span>`;
		} else {
			text = this.noSelectedLabel;
		}

		if ( this.insetLabel && this.selectedOptCounter > 0 ) {
			text = noSelectionText + text;
		}

		return text;
	}

	/**
	 * Checks if the dropdown is currently open (aria-expanded="true" and element is visible).
	 * @private
	 * @return {boolean} True if the dropdown is open, false otherwise.
	 */
	isDropdownOpen() {
		return (
			this.button.getAttribute( 'aria-expanded' ) === 'true' && this.isVisible( this.button )
		);
	}

	/**
	 * Determines if a DOM element is visible in the layout.
	 * @private
	 * @param {HTMLElement} element - The element to check.
	 * @return {boolean} True if element has layout (offset width/height), otherwise false.
	 */
	isVisible( element ) {
		return !! (
			element.offsetWidth ||
			element.offsetHeight ||
			element.getClientRects().length
		);
	}

	/**
	 * Moves focus to a specified element (checkbox or button),
	 * ensuring it has a valid tabindex and is actually focused.
	 * @private
	 * @param {HTMLElement} element - The element to focus.
	 */
	moveFocus( element ) {
		// Guard if no element found
		if ( ! element ) {
			return;
		}

		// Try to focus the element
		element.focus();

		// If the element did not receive focus, set tabindex and try again
		if ( element.ownerDocument.activeElement !== element ) {
			element.setAttribute( 'tabindex', '-1' ); // Temporarily make focusable
			element.focus();
		}
	}

	/**
	 * Cleans up the multi-select component by removing event listeners and restoring the native select.
	 * Call this method when removing the component from the DOM to prevent memory leaks.
	 */
	destroy() {
		// Clear any pending timeouts
		if ( this.focusTimeoutId ) {
			clearTimeout( this.focusTimeoutId );
		}
		if ( this.searchDebounceTimeout ) {
			clearTimeout( this.searchDebounceTimeout );
		}

		// Remove event listeners
		if ( this.boundWindowClickHandler ) {
			window.removeEventListener( 'click', this.boundWindowClickHandler );
		}
		if ( this.boundFocusOutHandler && this.wrapper ) {
			this.wrapper.removeEventListener( 'focusout', this.boundFocusOutHandler );
		}
		if ( this.boundLabelClickHandler && this.label ) {
			this.label.removeEventListener( 'click', this.boundLabelClickHandler );
		}
		if ( this.boundButtonClickHandler && this.button ) {
			this.button.removeEventListener( 'click', this.boundButtonClickHandler );
		}
		if ( this.boundButtonKeydownHandler && this.button ) {
			this.button.removeEventListener( 'keydown', this.boundButtonKeydownHandler );
		}
		if ( this.boundDropdownMousedownHandler && this.dropdown ) {
			this.dropdown.removeEventListener( 'mousedown', this.boundDropdownMousedownHandler );
		}
		if ( this.boundDropdownMouseupHandler && this.dropdown ) {
			this.dropdown.removeEventListener( 'mouseup', this.boundDropdownMouseupHandler );
		}
		if ( this.boundDropdownClickHandler && this.dropdown ) {
			this.dropdown.removeEventListener( 'click', this.boundDropdownClickHandler );
		}
		if ( this.boundDropdownKeydownHandler && this.dropdown ) {
			this.dropdown.removeEventListener( 'keydown', this.boundDropdownKeydownHandler );
		}
		if ( this.boundSelectChangeHandler && this.select ) {
			this.select.removeEventListener( 'change', this.boundSelectChangeHandler );
		}
		if ( this.boundSearchInputHandler && this.searchInput ) {
			this.searchInput.removeEventListener( 'input', this.boundSearchInputHandler );
		}
		if ( this.transitionEndCallback && this.dropdown ) {
			this.dropdown.removeEventListener( 'transitionend', this.transitionEndCallback );
		}

		// Remove custom elements
		if ( this.wrapper ) {
			this.wrapper.remove();
		}

		// Clear instance reference from element
		delete this.element._multiSelectInstance;

		// Clear references
		this.button = null;
		this.buttonLabel = null;
		this.dropdown = null;
		this.wrapper = null;
		this.customOptions = [];
		this.checkboxes.clear();
		this.searchInput = null;
		this.searchButton = null;
		this.noResults = null;
		this.boundWindowClickHandler = null;
		this.boundFocusOutHandler = null;
		this.boundLabelClickHandler = null;
		this.boundButtonClickHandler = null;
		this.boundButtonKeydownHandler = null;
		this.boundDropdownMousedownHandler = null;
		this.boundDropdownMouseupHandler = null;
		this.boundDropdownClickHandler = null;
		this.boundDropdownKeydownHandler = null;
		this.boundSelectChangeHandler = null;
		this.boundSearchInputHandler = null;
		this.transitionEndCallback = null;
		this.focusTimeoutId = null;
	}
}

// Export as ES6 module
export { MultiSelect };

// Export as global variable for non-module scripts
window.awc.MultiSelect = MultiSelect;
